import type { RequestOptions } from "../common/server";
import type { AuthenticationProviderType, AxShareIdentityProvider, AxShareSubInstanceInfo, EmailSettings, LdapSettings, ResponseObject, SetAxureRpRoleResponse, SetLicenseKey, SubInstanceOptions, UserAxureRpRole, UserCloudRole, UserType } from "@/generated/models";
import { objectToFormData } from "@/services/utils/formData";
import { server } from "./server";

export async function getSubInstanceInfo({ organizationId }: { organizationId: string }, { signal }: RequestOptions = {}) {
  const response = await server.get("admin/getSubInstanceInfo", {
    searchParams: {
      organizationId,
    },
    signal,
  });
  return await response.json() as AxShareSubInstanceInfo;
}

export interface SetUserAccessLevelModel {
  userIds: string;
  organizationId: string;
  accessLevel: number;
}

export async function setAccessLevel(options: { userIds: string[]; organizationId: string; accessLevel: UserType }) {
  const model: SetUserAccessLevelModel = {
    userIds: options.userIds.join(","),
    organizationId: options.organizationId,
    accessLevel: options.accessLevel,
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/setAccessLevel", { body: formData });
  return await response.json() as ResponseObject;
}

export interface SetUserAxureRpRoleModel {
  userIds: string;
  axureRpRole: number;
  assignSeats: boolean;
}

export async function setAxureRpRole(options: { userIds: string[]; axureRpRole: UserAxureRpRole; assignSeats: boolean }) {
  const model: SetUserAxureRpRoleModel = {
    userIds: options.userIds.join(","),
    axureRpRole: options.axureRpRole,
    assignSeats: options.assignSeats,
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/setAxureRpRole", { body: formData });
  return await response.json() as SetAxureRpRoleResponse;
}
export interface ChangeUserEmailModel {
  targetUserId: string;
  email: string;
}

export async function setEmail(options: { userId: string; email: string }) {
  const model: ChangeUserEmailModel = {
    targetUserId: options.userId,
    email: options.email,
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/setEmail", { body: formData });
  return await response.json() as ResponseObject;
}
export interface SetUserPasswordModel {
  targetUserId: string;
  password: string;
  pending: boolean;
}

export async function setPassword(options: { userId: string; password: string; requirePasswordChange: boolean }) {
  const model: SetUserPasswordModel = {
    targetUserId: options.userId,
    password: options.password,
    pending: options.requirePasswordChange,
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/setPassword", { body: formData });
  return await response.json() as ResponseObject;
}

export interface ChangeUserStateModel {
  userIds: string;
  active: boolean;
}

export async function changeUserState(options: { userIds: string[]; active: boolean }) {
  const model: ChangeUserStateModel = {
    userIds: options.userIds.join(","),
    active: options.active,
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/changeUserState", { body: formData });
  return await response.json() as ResponseObject;
}

export interface ChangeUserAuthenticationModel {
  userIds: string;
}

export async function addToSaml(options: { userIds: string[] }) {
  const model: ChangeUserAuthenticationModel = {
    userIds: options.userIds.join(","),
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/addToSaml", { body: formData });
  return await response.json() as ResponseObject;
}

export interface ChangeUserAuthenticationModel {
  userIds: string;
}

export async function removeFromSaml(options: { userIds: string[] }) {
  const model: ChangeUserAuthenticationModel = {
    userIds: options.userIds.join(","),
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/removeFromSaml", { body: formData });
  return await response.json() as ResponseObject;
}

export interface ChangeSeatsBulkModel {
  userIds: string;
  assignSeats: boolean;
}

export async function changeUserRpPublisherSeatStatusBulk(options: { userIds: string[]; assignSeats: boolean }) {
  const model: ChangeSeatsBulkModel = {
    userIds: options.userIds.join(","),
    assignSeats: options.assignSeats,
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/AssignRpPublisherSeatsBulk", { body: formData });
  return await response.json() as ResponseObject;
}

export interface AddUsersModel {
  userEmails: string;
  useSaml: boolean;
  accessLevel: number;
  axureRpRole: number;
  cloudRole?: number;
  password?: string;
  pending?: boolean;
  assignSeats: boolean;
}

export async function addAccounts(options: {
  userEmails: string[];
  useSaml: boolean;
  password?: string;
  pending?: boolean;
  systemRole: UserType;
  axureRpRole: UserAxureRpRole;
  cloudRole?: UserCloudRole;
}) {
  const model: AddUsersModel = {
    userEmails: options.userEmails.join(","),
    useSaml: options.useSaml,
    accessLevel: options.systemRole,
    axureRpRole: options.axureRpRole,
    cloudRole: options.cloudRole,
    password: options.password,
    pending: options.pending,
    assignSeats: true,
  };

  const formData = objectToFormData(model);
  const response = await server.post("admin/addAccounts", { body: formData });
  return await response.json() as ResponseObject;
}

export async function setSubInstanceOptions(options: SubInstanceOptions) {
  const formData = objectToFormData(options);
  const response = await server.post("admin/setSubInstanceOptions", { body: formData });
  return await response.json() as ResponseObject;
}

export async function getSamlSettings({ signal }: RequestOptions = {}) {
  const response = await server.get("admin/getSamlSettings", {
    signal,
  });
  return await response.json() as AxShareIdentityProvider;
}

export async function clearSaml() {
  const response = await server.post("admin/clearSaml");
  return await response.json() as ResponseObject;
}

export async function getLdapSettings({ signal }: RequestOptions = {}) {
  const response = await server.get("admin/getLdapSettings", {
    signal,
  });
  return await response.json() as LdapSettings;
}

export async function getEmailSettings({ signal }: RequestOptions = {}) {
  const response = await server.get("admin/getEmailSettings", {
    signal,
  });
  return await response.json() as EmailSettings;
}

export async function submitLdap(ldapSettings: LdapSettings, certificate?: File) {
  const formData = objectToFormData({
    settings: ldapSettings,
    certFile: certificate,
  });
  const response = await server.post("admin/submitLdap", { body: formData });
  return await response.json() as ResponseObject;
}

export async function submitEmail(emailSettings: EmailSettings) {
  const formData = objectToFormData(emailSettings);
  const response = await server.post("admin/submitEmail", { body: formData });
  return await response.json() as ResponseObject;
}

export async function sendTestEmail(emailSettings: EmailSettings, recipient: string) {
  const formData = objectToFormData({ ...emailSettings, recipient });
  const response = await server.post("admin/sendTestEmail", { body: formData });
  return await response.json() as ResponseObject;
}

export async function setAuthenticationProvider(authenticationProvider: AuthenticationProviderType) {
  const formData = objectToFormData({
    authenticationProvider,
  });
  const response = await server.post("admin/setAuthenticationProvider", { body: formData });
  return await response.json() as ResponseObject;
}

export async function updateAllowActiveDirectoryCreateNewAccountsAutomatically(allow: boolean) {
  const formData = objectToFormData({ allow });
  const response = await server.post("admin/updateAllowActiveDirectoryCreateNewAccountsAutomatically", { body: formData });
  return await response.json() as ResponseObject;
}

export async function updateAllowLdapCreateNewAccountsAutomatically(allow: boolean) {
  const formData = objectToFormData({ allow });
  const response = await server.post("admin/updateAllowLdapCreateNewAccountsAutomatically", { body: formData });
  return await response.json() as ResponseObject;
}

export async function testLdapAuthentication(ldapSettings: LdapSettings, login: string, password: string, certificate?: File) {
  const formData = objectToFormData({
    settings: ldapSettings,
    login,
    password,
    certFile: certificate,
  });
  const response = await server.post("admin/testLdapAuthentication", { body: formData });
  return await response.json() as ResponseObject;
}

export async function clearActiveDirectory() {
  const response = await server.post("admin/clearActiveDirectorySettings");
  return await response.json() as ResponseObject;
}

export async function clearLdap() {
  const response = await server.post("admin/clearLdapSettings");
  return await response.json() as ResponseObject;
}

export async function getLicenseKey({ signal }: RequestOptions = {}) {
  const response = await server.get("admin/getLicenseKey", { signal });
  return await response.json() as string;
}

export async function setLicenseKey(licenseKey?: string) {
  const formData = objectToFormData({ licenseKey });
  const response = await server.post("admin/setLicenseKey", { body: formData });
  return await response.json() as SetLicenseKey;
}
